import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Header } from '../header/Header';
import { zynity_api } from '../../config/external-routes';
import './EngagementSessionPage.css';

const SESSION_TYPE = 'team_setup';

type SessionState = {
  id: number;
  status: string;
  session_type: string;
  current_step_key: string | null;
  current_step: any;
  context_json: Record<string, any>;
  progress: {
    total_steps: number;
    completed_steps: number;
    remaining_steps: number;
    current_step_key: string | null;
  };
};

type ChatEntry =
  | { id: string; role: 'assistant' | 'user' | 'system' | 'error'; content: string; typing?: boolean };

const STEP_PROMPTS: Record<string, string> = {
  define_team_name: 'What should we call this team?',
  define_purpose: 'What is the core purpose for this team right now?',
  set_initial_focus: 'What is the first focus area this team should align on?',
  review_and_confirm: 'Review your answers and confirm this setup session.',
};

const STEP_ORDER = [
  'define_team_name',
  'define_purpose',
  'set_initial_focus',
  'review_and_confirm',
];

function titleFromStepKey(stepKey: string): string {
  return stepKey
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function promptFor(stepKey: string, fallback?: string): string {
  return STEP_PROMPTS[stepKey] || fallback || titleFromStepKey(stepKey);
}

function formatResponse(value: any): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatResponse(item)).filter(Boolean).join('\n');
  }

  if (typeof value === 'object') {
    const values = Object.values(value)
      .map((item) => formatResponse(item).trim())
      .filter(Boolean);
    return values.join('\n');
  }

  return String(value);
}

export const EngagementSessionPage: React.FC = () => {
  const user = useSelector((state: any) => state.auth);
  const enabled = !!user?.engagement_engine_enabled;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState<SessionState | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [assistantTyping, setAssistantTyping] = useState(false);
  const [pendingUserAnswer, setPendingUserAnswer] = useState<string>('');
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentStep = session?.current_step || null;
  const fields = useMemo(() => {
    const cfg = currentStep?.config_json;
    if (!cfg || !Array.isArray(cfg.fields)) return [];
    return cfg.fields;
  }, [currentStep]);

  const hydrateForm = useCallback((nextSession: SessionState) => {
    const stepKey = nextSession?.current_step_key;
    const stepFields = nextSession?.current_step?.config_json?.fields;
    if (!stepKey || !Array.isArray(stepFields)) {
      setFormValues({});
      return;
    }

    const existing = (nextSession.context_json && nextSession.context_json[stepKey]) || {};
    const nextValues: Record<string, string> = {};
    stepFields.forEach((field: any) => {
      const key = String(field?.key || '');
      if (!key) return;
      nextValues[key] = existing[key] != null ? String(existing[key]) : '';
    });
    setFormValues(nextValues);
  }, []);

  const applySession = useCallback((payload: any) => {
    const nextSession = payload?.session as SessionState;
    if (!nextSession) return;
    setSession(nextSession);
    hydrateForm(nextSession);
  }, [hydrateForm]);

  const revealNextPromptWithTyping = useCallback((payload: any, delayMs = 900) => {
    setAssistantTyping(true);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      applySession(payload);
      setPendingUserAnswer('');
      setAssistantTyping(false);
      typingTimerRef.current = null;
    }, delayMs);
  }, [applySession]);

  const startSession = useCallback(async () => {
    const response = await axios.post(zynity_api.engagement_session_start, {
      session_type: SESSION_TYPE,
    });
    applySession(response.data);
  }, [applySession]);

  const loadSession = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.get(zynity_api.engagement_session_current, {
        params: { session_type: SESSION_TYPE },
      });
      const currentSession = response?.data?.session;
      if (currentSession?.status === 'paused') {
        await startSession();
        setMessage('Session resumed.');
      } else {
        applySession(response.data);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        await startSession();
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Unable to load engagement session.';
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, applySession, startSession]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session, formValues, message, error, submitting, assistantTyping, pendingUserAnswer]);

  const chatHistory = useMemo(() => {
    const context = session?.context_json || {};
    const currentStepKey = session?.current_step_key;

    return Object.entries(context)
      .filter(([stepKey, stepResponse]) => {
        if (!stepKey || stepKey === currentStepKey) return false;
        if (!stepResponse || typeof stepResponse !== 'object' || Array.isArray(stepResponse)) return false;
        const formatted = formatResponse(stepResponse);
        return formatted.trim() !== '';
      })
      .map(([stepKey, stepResponse]) => ({
        stepKey,
        prompt: promptFor(stepKey),
        answer: formatResponse(stepResponse),
      }))
      .sort((a, b) => {
        const aIndex = STEP_ORDER.indexOf(a.stepKey);
        const bIndex = STEP_ORDER.indexOf(b.stepKey);
        const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (aRank !== bRank) {
          return aRank - bRank;
        }
        return a.stepKey.localeCompare(b.stepKey);
      });
  }, [session]);

  const onChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const validateRequired = () => {
    for (const field of fields) {
      if (!field?.required) continue;
      const key = String(field?.key || '');
      const value = (formValues[key] || '').trim();
      if (!value) {
        return `${field?.label || key} is required.`;
      }
    }
    return '';
  };

  const submitStep = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!session?.id || !session?.current_step_key) return;

    const validationError = validateRequired();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setAssistantTyping(true);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    setError('');
    setMessage('');
    setPendingUserAnswer(formatResponse(formValues));

    try {
      const response = await axios.post(zynity_api.engagement_session_step_submit, {
        session_id: session.id,
        step_key: session.current_step_key,
        response_json: formValues,
      });
      const nextSession = response?.data?.session;
      if (nextSession?.status === 'completed') {
        applySession(response.data);
        setAssistantTyping(false);
        setPendingUserAnswer('');
        setMessage('Great. Your team setup session is completed.');
      }
      if (nextSession?.status !== 'completed') {
        setFormValues({});
        revealNextPromptWithTyping(response.data);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to submit step.';
      setError(msg);
      setPendingUserAnswer('');
      setAssistantTyping(false);
    } finally {
      setSubmitting(false);
    }
  };

  const isCompleted = session?.status === 'completed';
  const stepProgress = session?.progress
    ? isCompleted
      ? `Completed ${Math.max(1, session.progress.total_steps)} of ${Math.max(1, session.progress.total_steps)}`
      : `Step ${Math.min(
        Math.max(1, session.progress.total_steps),
        session.progress.completed_steps + 1
      )} of ${Math.max(1, session.progress.total_steps)}`
    : '';

  const currentPrompt = session?.current_step_key
    ? promptFor(session.current_step_key, currentStep?.title)
    : 'Continue your team setup.';

  const chatEntries = useMemo(() => {
    const entries: ChatEntry[] = [
      {
        id: 'intro',
        role: 'assistant',
        content: "I will guide you step by step to set up your team. Let's begin.",
      },
    ];

    chatHistory.forEach((item, idx) => {
      entries.push({
        id: `h-q-${item.stepKey}-${idx}`,
        role: 'assistant',
        content: item.prompt,
      });
      entries.push({
        id: `h-a-${item.stepKey}-${idx}`,
        role: 'user',
        content: item.answer,
      });
    });

    if (!isCompleted) {
      // Keep the active question visible at all times so it never disappears after submit.
      entries.push({
        id: `current-q-${session?.current_step_key || 'unknown'}`,
        role: 'assistant',
        content: currentPrompt,
      });
    }

    if (assistantTyping && pendingUserAnswer) {
      entries.push({
        id: 'pending-user-answer',
        role: 'user',
        content: pendingUserAnswer,
      });
    }

    if (!isCompleted && assistantTyping) {
      entries.push({
        id: 'typing-indicator',
        role: 'assistant',
        content: '...',
        typing: true,
      });
    }

    if (isCompleted) {
      entries.push({
        id: 'completed-msg',
        role: 'assistant',
        content: 'Session complete. Your setup answers are saved.',
      });
    }

    if (message) {
      entries.push({ id: 'system-msg', role: 'system', content: message });
    }
    if (error) {
      entries.push({ id: 'error-msg', role: 'error', content: error });
    }

    return entries as ChatEntry[];
  }, [
    chatHistory,
    assistantTyping,
    pendingUserAnswer,
    isCompleted,
    currentPrompt,
    session?.current_step_key,
    message,
    error,
  ]);

  if (!enabled) {
    return (
      <>
        <Header />
        <main className="container mt-4 engagement-page">
          <div className="engagement-card">
            <h1 className="engagement-title">Engagement Engine</h1>
            <p className="engagement-subtitle">
              This feature is not enabled for your account.
            </p>
            <Link to="/" className="btn btn-outline-secondary">Return to Home</Link>
          </div>
        </main>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <main className="container mt-4 engagement-page">
          <div className="engagement-card">Loading engagement session...</div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="container mt-4 engagement-page">
        <section className="engagement-chat-shell" aria-label="Engagement chat">
          <header className="engagement-chat-header">
            <h1 className="engagement-title">Engagement Engine</h1>
            <p className="engagement-subtitle">Guided team setup conversation</p>
            {stepProgress && <p className="engagement-progress">{stepProgress}</p>}
          </header>

          <div className="engagement-chat-feed">
            {chatEntries.map((entry) => {
              if (entry.typing) {
                return (
                  <article key={entry.id} className="bubble bubble-assistant bubble-typing" aria-live="polite">
                    <span className="typing-dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  </article>
                );
              }

              if (entry.role === 'user') {
                return <article key={entry.id} className="bubble bubble-user">{entry.content}</article>;
              }
              if (entry.role === 'system') {
                return <article key={entry.id} className="bubble bubble-system">{entry.content}</article>;
              }
              if (entry.role === 'error') {
                return <article key={entry.id} className="bubble bubble-error">{entry.content}</article>;
              }

              return <article key={entry.id} className="bubble bubble-assistant">{entry.content}</article>;
            })}

            <div ref={chatEndRef} />
          </div>

          {!isCompleted && (
            <form className="engagement-composer" onSubmit={submitStep}>
              {fields.length > 0 ? (
                <div className="engagement-composer-fields">
                  {fields.map((field: any) => {
                    const key = String(field?.key || '');
                    const label = field?.label || key || 'Response';
                    const type = field?.type || 'text';
                    const required = !!field?.required;
                    if (!key) return null;

                    if (type === 'textarea') {
                      return (
                        <textarea
                          key={key}
                          id={key}
                          placeholder={label}
                          value={formValues[key] || ''}
                          onChange={onChange(key)}
                          required={required}
                          disabled={submitting || assistantTyping}
                        />
                      );
                    }

                    return (
                      <input
                        key={key}
                        id={key}
                        type="text"
                        placeholder={label}
                        value={formValues[key] || ''}
                        onChange={onChange(key)}
                        required={required}
                        disabled={submitting || assistantTyping}
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="engagement-confirm-note">Confirm to continue.</p>
              )}

              <button className="btn btn-primary" type="submit" disabled={submitting || assistantTyping}>
                {submitting ? 'Sending...' : fields.length > 0 ? 'Send' : 'Confirm'}
              </button>
            </form>
          )}

          {isCompleted && (
            <div className="engagement-complete-actions">
              <Link to="/" className="btn btn-primary">Return to Home</Link>
            </div>
          )}
        </section>
      </main>
    </>
  );
};
